import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ArrowRight, InfoIcon, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "wouter";

export function AnalysisNarrative({
  conclusion,
  explanation,
  evidence,
  label,
}: {
  conclusion: string;
  explanation?: string;
  evidence?: string;
  label?: string;
}) {
  return (
    <Card className="border-[#9F86C0]/40 bg-[#9F86C0]/10 shadow-none">
      <CardContent className="flex gap-4 p-5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-[#5E548E]/15 text-[#5E548E]">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="space-y-1.5">
          {label && (
            <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500">
              {label}
            </p>
          )}
          {label ? (
            <h2 className="text-lg font-semibold leading-6 text-[#231942]">{conclusion}</h2>
          ) : (
            <p className="text-sm font-semibold text-[#231942]">{conclusion}</p>
          )}
          {explanation && <p className="text-sm text-gray-700">{explanation}</p>}
          {evidence && <p className="text-sm text-gray-700">{evidence}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export function AnalysisInfoTooltip({
  compact = false,
  label,
  text,
}: {
  compact?: boolean;
  label: string;
  text: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`${label} info`}
          className={cn(
            "inline-flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-50 hover:text-gray-700",
            compact ? "h-4 w-4" : "h-6 w-6",
          )}
        >
          <InfoIcon className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs leading-5">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

export function InsightSectionHeader({
  entity,
  heading,
  id,
  label,
  links = [],
  supporting,
}: {
  entity?: {
    prefix: string;
    suffix?: string;
    value: string;
  };
  heading: string;
  id: string;
  label: string;
  links?: Array<{
    href: string;
    label: string;
  }>;
  supporting?: string;
}) {
  return (
    <div className="min-w-0 flex-1 space-y-7">
      <div className="flex items-center gap-3">
        <div aria-hidden="true" className="h-px flex-1 bg-gray-200" />
        <p className="shrink-0 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500">
          {label}
        </p>
        <div aria-hidden="true" className="h-px flex-1 bg-gray-200" />
      </div>
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div className="max-w-4xl space-y-1.5">
          <h2 id={id} className="text-lg font-semibold leading-6 text-[#231942]">
            {entity ? (
              <>
                {entity.prefix}{" "}
                <span className="rounded bg-[#9F86C0]/15 px-1.5 py-0.5 text-[#5E548E]">
                  {entity.value}
                </span>
                {entity.suffix ? ` ${entity.suffix}` : ""}
              </>
            ) : heading}
          </h2>
          {supporting && (
            <p className="text-sm leading-5 text-gray-600">{supporting}</p>
          )}
        </div>
        {links.length > 0 && (
          <div className="flex shrink-0 flex-wrap gap-2">
            {links.map(link => (
              <Button key={`${link.href}:${link.label}`} variant="outline" size="sm" asChild>
                <Link href={link.href}>
                  {link.label}
                  <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Link>
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
